// client/src/coms/interactions/InteractionCard.js
// The card shown in place of an interaction message's text (ChatMessageRow, system_kind 'interaction').
// Data comes from interactionStore (shaped by the server for this member); actions call the API and apply the
// response at once, and every other viewer refreshes from the channel stream's interaction_update event.
// The card is compact; "Details" expands it in place (voters of public polls, every question, comments).
import React, { useEffect, useMemo, useState } from 'react';
import { comsApiGet } from '../comsApi';
import { openTarget } from '../organisation/orgShared';
import { TYPE_META, errorText, interactionApi, serverNow, useInteraction, useServerClock } from './interactionStore';
import './interactions.css';

const SUGGESTION_STATUSES = [
  ['new', 'New'],
  ['under_review', 'Under review'],
  ['planned', 'Planned'],
  ['in_progress', 'In progress'],
  ['completed', 'Completed'],
  ['declined', 'Declined'],
];
const OUTCOME_LABEL = { approved: 'Approved', rejected: 'Rejected', no_quorum: 'No quorum' };

function localZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function formatWhen(ms, timeZone) {
  if (!ms) return '';
  try {
    return new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', ...(timeZone ? { timeZone } : {}) }).format(new Date(ms));
  } catch {
    return new Date(ms).toLocaleString();
  }
}

export function formatDuration(msLeft) {
  const s = Math.max(0, Math.floor(msLeft / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

function useBusy() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function run(fn) {
    if (busy) return null;
    setBusy(true);
    setErr('');
    try {
      return await fn();
    } catch (e) {
      setErr(errorText(e));
      return null;
    } finally {
      setBusy(false);
    }
  }
  return { busy, err, setErr, run };
}

function joinVoice(it, channelId) {
  try {
    window.dispatchEvent(new CustomEvent('vox-join-voice', { detail: { server_key: it.server_key, channel_id: Number(channelId) } }));
  } catch {}
}

// ---------- shared header / footer ----------

function StatusChip({ it, now }) {
  if (it.type === 'event') {
    if (it.status === 'open') return <span className="vxi-chip tone">Finding a time</span>;
    if (it.status === 'closed') return <span className="vxi-chip">Ended</span>;
    const ev = it.event;
    if (!ev) return <span className="vxi-chip">Confirmed</span>;
    if (ev.status === 'cancelled') return <span className="vxi-chip bad">Cancelled</span>;
    if (now >= ev.start_ms && now < ev.end_ms) return <span className="vxi-chip good">● Live now</span>;
    if (now >= ev.end_ms) return <span className="vxi-chip">Ended</span>;
    return <span className="vxi-chip good">Confirmed</span>;
  }
  if (it.type === 'countdown') return it.countdown && (it.countdown.reached || it.status === 'closed') ? <span className="vxi-chip">{it.countdown.ended_early ? 'Ended' : 'Reached'}</span> : <span className="vxi-chip tone">Counting down</span>;
  if (it.type === 'suggestion') return null;
  if (!it.open || it.status === 'closed') return <span className="vxi-chip">Closed</span>;
  if (it.closes_at_ms) return <span className="vxi-chip warn" title={formatWhen(it.closes_at_ms)}>Ends in {formatDuration(it.closes_at_ms - now)}</span>;
  return <span className="vxi-chip good">Open</span>;
}

function ManageBar({ it, expanded, setExpanded, onEdit, run, busy }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const p = it.perms || {};
  const closable = it.status !== 'closed' && p.can_close && it.type !== 'suggestion';
  const reopenable = it.status === 'closed' && p.can_close && ['poll', 'decision', 'qa', 'tasks', 'suggestion'].includes(it.type);
  return (
    <div className="vxi-actions" role="group" aria-label="Interaction tools">
      <button type="button" className="vxi-btn small ghost" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
        {expanded ? 'Less' : 'Details'}
      </button>
      {p.can_edit && it.status !== 'closed' ? (
        <button type="button" className="vxi-btn small ghost" onClick={onEdit} disabled={busy}>
          Edit
        </button>
      ) : null}
      {closable ? (
        <button type="button" className="vxi-btn small ghost" disabled={busy} onClick={() => run(() => interactionApi.close(it.id))}>
          {it.type === 'event' ? 'End event' : it.type === 'countdown' ? 'End' : it.type === 'tasks' ? 'Archive list' : 'Close'}
        </button>
      ) : null}
      {it.type === 'suggestion' && it.status !== 'closed' && p.can_close ? (
        <button type="button" className="vxi-btn small ghost" disabled={busy} onClick={() => run(() => interactionApi.close(it.id))}>
          Close
        </button>
      ) : null}
      {reopenable ? (
        <button type="button" className="vxi-btn small ghost" disabled={busy} onClick={() => run(() => interactionApi.reopen(it.id))}>
          Reopen
        </button>
      ) : null}
      {p.can_delete ? (
        confirmDelete ? (
          <>
            <button type="button" className="vxi-btn small danger" disabled={busy} onClick={() => run(() => interactionApi.remove(it.id))}>
              Delete for everyone
            </button>
            <button type="button" className="vxi-btn small ghost" onClick={() => setConfirmDelete(false)}>
              Keep
            </button>
          </>
        ) : (
          <button type="button" className="vxi-btn small ghost danger" onClick={() => setConfirmDelete(true)}>
            Delete
          </button>
        )
      ) : null}
    </div>
  );
}

function EditForm({ it, onDone }) {
  const [title, setTitle] = useState(it.title);
  const [description, setDescription] = useState(it.description || '');
  const timed = ['poll', 'decision', 'qa', 'countdown'].includes(it.type);
  const toLocalInput = (ms) => {
    if (!ms) return '';
    const d = new Date(ms);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [closes, setCloses] = useState(toLocalInput(it.type === 'countdown' ? it.countdown && it.countdown.target_ms : it.closes_at_ms));
  const { busy, err, run } = useBusy();
  return (
    <form
      className="vxi-dialog-body"
      style={{ padding: '8px 0 0' }}
      onSubmit={(e) => {
        e.preventDefault();
        run(async () => {
          const body = { title, description };
          if (timed) body.closes_at_ms = closes ? new Date(closes).getTime() : 0;
          await interactionApi.edit(it.id, body);
          onDone();
        });
      }}
    >
      <label className="vxi-field">
        <span>Title</span>
        <input type="text" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} required />
      </label>
      <label className="vxi-field">
        <span>Description</span>
        <textarea value={description} maxLength={2000} onChange={(e) => setDescription(e.target.value)} rows={2} />
      </label>
      {timed ? (
        <label className="vxi-field">
          <span>{it.type === 'countdown' ? 'Target time' : 'Closes at (empty = no closing time)'}</span>
          <input type="datetime-local" className="vxi-input" value={closes} onChange={(e) => setCloses(e.target.value)} required={it.type === 'countdown'} />
        </label>
      ) : null}
      <div className="vxi-actions">
        <button type="submit" className="vxi-btn primary" disabled={busy || !title.trim()}>
          Save
        </button>
        <button type="button" className="vxi-btn" onClick={onDone}>
          Cancel
        </button>
      </div>
      {err ? <div className="vxi-error" role="alert">{err}</div> : null}
    </form>
  );
}

// ---------- polls ----------

function PollBody({ it, run, busy }) {
  const mode = it.config.mode || 'single';
  const mine = Array.isArray(it.my.choice) ? it.my.choice : [];
  const [draft, setDraft] = useState(null); // multiple: selected ids; ranked: ordered ids
  const editing = draft != null;
  const selected = editing ? draft : mine;
  const canVote = it.perms.can_vote && (it.perms.can_change_vote || !it.my.voted);
  const winners = (it.result && it.result.winners) || [];
  const showWinner = !it.open && it.results_visible;

  function clickOption(id) {
    if (!canVote) return;
    if (mode === 'single') {
      if (mine.length === 1 && mine[0] === id) return;
      run(() => interactionApi.vote(it.id, { choices: [id] }));
      return;
    }
    const cur = editing ? draft : mine.slice();
    if (mode === 'multiple') {
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : cur.length < (it.config.max_selections || 99) ? [...cur, id] : cur;
      setDraft(next);
    } else {
      setDraft(cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
    }
  }

  return (
    <>
      <div className="vxi-options" role={mode === 'single' ? 'radiogroup' : 'group'} aria-label={it.title}>
        {it.options.map((o) => {
          const isMine = selected.includes(o.id);
          const rank = mode === 'ranked' ? selected.indexOf(o.id) + 1 : 0;
          const pct = it.results_visible && typeof o.pct === 'number' ? o.pct : null;
          const tone = o.color && /^#[0-9a-f]{6}$/i.test(o.color) ? `${parseInt(o.color.slice(1, 3), 16)}, ${parseInt(o.color.slice(3, 5), 16)}, ${parseInt(o.color.slice(5, 7), 16)}` : null;
          return (
            <button
              key={o.id}
              type="button"
              className={`vxi-option${isMine ? ' is-mine' : ''}${showWinner && winners.includes(o.id) ? ' is-winner' : ''}`}
              role={mode === 'single' ? 'radio' : 'checkbox'}
              aria-checked={isMine}
              disabled={!canVote || busy}
              onClick={() => clickOption(o.id)}
              style={tone ? { '--vxi-bar': tone } : undefined}
            >
              {pct != null ? <span className="vxi-bar" style={{ width: `${pct}%` }} aria-hidden="true" /> : null}
              <span className={`vxi-mark${mode === 'multiple' ? ' square' : ''}`} aria-hidden="true">
                {mode === 'ranked' && rank ? rank : isMine ? '✓' : ''}
              </span>
              <span className="vxi-option-label">
                {o.color ? <span className="vxi-swatch" style={{ background: o.color }} aria-hidden="true" /> : null}
                {o.emoji ? `${o.emoji} ` : ''}
                {o.label}
                {showWinner && winners.includes(o.id) ? ' 🏆' : ''}
              </span>
              <span className="vxi-option-stat">{pct != null ? `${Math.round(pct)}% · ${o.votes}` : ''}</span>
              {Array.isArray(o.voters) && o.voters.length ? <span className="vxi-voters">{o.voters.join(', ')}</span> : null}
            </button>
          );
        })}
      </div>
      {editing ? (
        <div className="vxi-actions">
          <button type="button" className="vxi-btn primary" disabled={busy || !draft.length} onClick={() => run(async () => { await interactionApi.vote(it.id, mode === 'ranked' ? { ranking: draft } : { choices: draft }); setDraft(null); })}>
            {mode === 'ranked' ? 'Submit ranking' : 'Vote'}
          </button>
          <button type="button" className="vxi-btn" onClick={() => setDraft(null)}>
            Cancel
          </button>
          <span className="vxi-muted">{mode === 'ranked' ? 'Click choices in order of preference.' : `Choose up to ${it.config.max_selections}.`}</span>
        </div>
      ) : it.my.voted && canVote && it.perms.can_change_vote ? (
        <div className="vxi-actions">
          <button type="button" className="vxi-link" onClick={() => run(() => interactionApi.unvote(it.id))} disabled={busy}>
            Remove my vote
          </button>
        </div>
      ) : null}
      {it.result && it.result.round_count > 1 && it.results_visible ? <div className="vxi-muted" style={{ marginTop: 6 }}>Ranked-choice result after {it.result.round_count} rounds (bars show first preferences).</div> : null}
      {Array.isArray(it.result && it.result.rounds) && it.results_visible ? (
        <details className="vxi-details">
          <summary>Counting rounds</summary>
          <ol className="vxi-muted" style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {it.result.rounds.map((r, i) => (
              <li key={i}>
                {r.counts.map((c) => `${(it.options.find((o) => o.id === c.id) || {}).label}: ${c.votes}`).join(' · ')}
                {r.eliminated ? ` — ${(it.options.find((o) => o.id === r.eliminated) || {}).label} eliminated` : ''}
                {r.exhausted ? ` (${r.exhausted} exhausted)` : ''}
              </li>
            ))}
          </ol>
        </details>
      ) : null}
    </>
  );
}

// ---------- decisions ----------

function DecisionBody({ it, run, busy }) {
  const counts = Object.fromEntries(it.options.map((o) => [o.label.toLowerCase(), o]));
  const mine = it.my.choice ? String(it.my.choice) : '';
  const canVote = it.perms.can_vote && (it.perms.can_change_vote || !it.my.voted);
  const r = it.result;
  return (
    <>
      <div className="vxi-tally" role="radiogroup" aria-label="Your vote">
        {['yes', 'no', 'abstain'].map((k) => (
          <button key={k} type="button" role="radio" aria-checked={mine === k} disabled={!canVote || busy || mine === k} onClick={() => run(() => interactionApi.vote(it.id, { choice: k }))}>
            {k === 'yes' ? '👍 Yes' : k === 'no' ? '👎 No' : '➖ Abstain'}
            {it.results_visible && counts[k] ? <small>{counts[k].votes} {counts[k].votes === 1 ? 'vote' : 'votes'}</small> : null}
          </button>
        ))}
      </div>
      <div className="vxi-meta">
        <span>Needs {it.config.approval_pct}% Yes of Yes + No</span>
        {it.config.quorum ? <span>Quorum: {it.config.quorum} votes</span> : null}
        <span>{it.config.anonymous ? 'Private ballots' : 'Public ballots'}</span>
        {!it.results_visible ? <span>Tally shown when voting closes</span> : null}
      </div>
      {r ? (
        <div className={`vxi-outcome ${r.outcome}`} role="status">
          {r.final ? 'Outcome: ' : 'Currently: '}
          {OUTCOME_LABEL[r.outcome]} · {r.yes} yes, {r.no} no, {r.abstain} abstain ({r.yes_share}% yes)
        </div>
      ) : null}
      {it.my.voted && canVote && it.perms.can_change_vote && it.open ? (
        <div className="vxi-actions">
          <button type="button" className="vxi-link" onClick={() => run(() => interactionApi.unvote(it.id))} disabled={busy}>
            Withdraw my vote
          </button>
        </div>
      ) : null}
    </>
  );
}

// ---------- events ----------

function EventBody({ it, run, busy, now }) {
  const zone = localZone();
  const orgZone = it.config.timezone && it.config.timezone !== zone ? it.config.timezone : '';
  const ev = it.event;
  const voiceId = it.config.voice_channel_id;
  if (it.status === 'open') {
    const mine = it.my.availability || {};
    const best = Math.max(0, ...it.options.map((o) => o.yes));
    return (
      <>
        <div className="vxi-options">
          {it.options.map((o) => {
            const v = mine[String(o.id)] || '';
            const set = (val) => run(() => interactionApi.vote(it.id, { availability: { ...mine, [o.id]: val } }));
            return (
              <div key={o.id} className="vxi-slot">
                <div>
                  <div className="vxi-slot-when">
                    {formatWhen(o.starts_at_ms)} {best > 0 && o.yes === best ? '⭐' : ''}
                  </div>
                  <div className="vxi-muted">
                    {o.yes} available · {o.maybe} maybe{orgZone ? ` · ${formatWhen(o.starts_at_ms, orgZone)} (${orgZone})` : ''}
                    {Array.isArray(o.available) && o.available.length ? ` — ${o.available.join(', ')}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <span className="vxi-seg" role="group" aria-label={`Availability for ${formatWhen(o.starts_at_ms)}`}>
                    {[['yes', 'Yes'], ['maybe', 'Maybe'], ['no', 'No']].map(([val, label]) => (
                      <button key={val} type="button" aria-pressed={v === val} disabled={busy || !it.perms.can_vote} onClick={() => set(val)}>
                        {label}
                      </button>
                    ))}
                  </span>
                  {it.perms.can_confirm ? (
                    <button type="button" className="vxi-btn small" disabled={busy} onClick={() => run(() => interactionApi.confirm(it.id, o.id))}>
                      Confirm
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
        <div className="vxi-meta">
          <span>Times in your time zone ({zone})</span>
          {it.closes_at_ms ? <span>Voting closes {formatWhen(it.closes_at_ms)}</span> : null}
          {it.config.max_attendees ? <span>Up to {it.config.max_attendees} people</span> : null}
          {it.config.repeat && it.config.repeat !== 'none' ? <span>Repeats {it.config.repeat}</span> : null}
        </div>
      </>
    );
  }
  if (!ev) return <div className="vxi-empty">This event has ended.</div>;
  const live = now >= ev.start_ms && now < ev.end_ms;
  const joinable = voiceId && now >= ev.start_ms - 15 * 60000 && now < ev.end_ms && ev.status !== 'cancelled';
  const rsvp = ev.my_rsvp;
  return (
    <>
      <div className="vxi-slot" style={{ marginTop: 8 }}>
        <div>
          <div className="vxi-slot-when" style={{ fontSize: 15 }}>{formatWhen(ev.start_ms)}</div>
          <div className="vxi-muted">
            {live ? 'Happening now' : now < ev.start_ms ? `Starts in ${formatDuration(ev.start_ms - now)}` : 'Ended'}
            {orgZone ? ` · ${formatWhen(ev.start_ms, orgZone)} (${orgZone})` : ''}
            {it.config.location ? ` · ${it.config.location}` : ''}
            {it.config.repeat && it.config.repeat !== 'none' ? ` · repeats ${it.config.repeat} (#${ev.occurrence})` : ''}
          </div>
        </div>
        {joinable ? (
          <button type="button" className="vxi-btn primary" onClick={() => joinVoice(it, voiceId)}>
            🔊 Join voice
          </button>
        ) : null}
      </div>
      {it.perms.can_rsvp ? (
        <div className="vxi-actions" role="group" aria-label="RSVP">
          {[['going', `Going · ${ev.counts.going}`], ['maybe', `Maybe · ${ev.counts.maybe}`], ['not_going', `Not going · ${ev.counts.not_going}`]].map(([val, label]) => (
            <button key={val} type="button" className={`vxi-btn${rsvp === val ? ' active' : ''}`} aria-pressed={rsvp === val} disabled={busy || (val === 'going' && ev.full && rsvp !== 'going')} onClick={() => run(() => interactionApi.rsvp(it.id, rsvp === val ? 'none' : val))}>
              {label}
            </button>
          ))}
        </div>
      ) : (
        <div className="vxi-meta">
          <span>{ev.counts.going} went · {ev.counts.maybe} maybe</span>
        </div>
      )}
      <div className="vxi-meta">
        {it.config.max_attendees ? <span>{ev.counts.going}/{it.config.max_attendees} going{ev.full ? ' · full' : ''}</span> : null}
        {it.config.reminders && it.config.reminders.length ? <span>Reminders {it.config.reminders.map((m) => (m >= 60 ? `${Math.round(m / 60)}h` : `${m}m`)).join(', ')} before</span> : null}
        {voiceId && !joinable ? <span>Voice channel linked</span> : null}
      </div>
      {Array.isArray(ev.attendees) && ev.attendees.length ? <div className="vxi-muted" style={{ marginTop: 6 }}>{ev.attendees.map((a) => `${a.name}${a.rsvp === 'maybe' ? ' (maybe)' : ''}`).join(', ')}</div> : null}
    </>
  );
}

// ---------- tasks ----------

function TasksBody({ it, run, busy, expanded }) {
  const [text, setText] = useState('');
  const [members, setMembers] = useState(null);
  const [editingId, setEditingId] = useState(0);
  const [editText, setEditText] = useState('');
  const canEdit = !!it.perms.can_edit_items;
  const items = it.items || [];
  const pct = it.progress && it.progress.total ? Math.round((it.progress.done / it.progress.total) * 100) : 0;
  const loadMembers = () => {
    if (members) return;
    interactionApi.assignable(it.id).then((r) => setMembers(r.members || [])).catch(() => setMembers([]));
  };
  const move = (idx, dir) => {
    const ids = items.map((x) => x.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    run(() => interactionApi.reorder(it.id, ids));
  };
  const shown = expanded ? items : items.slice(0, 8);
  return (
    <>
      <div className="vxi-meta" style={{ marginTop: 4 }}>
        <span>
          {it.progress.done}/{it.progress.total} done
        </span>
      </div>
      <div className="vxi-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Progress">
        <div style={{ width: `${pct}%` }} />
      </div>
      {items.length ? (
        <div className={`vxi-rows${expanded ? ' vxi-scroll' : ''}`}>
          {shown.map((t, idx) => (
            <div key={t.id} className={`vxi-row${t.done ? ' is-done' : ''}`}>
              <button type="button" className="vxi-check" role="checkbox" aria-checked={t.done} aria-label={`${t.done ? 'Reopen' : 'Complete'}: ${t.body}`} disabled={busy || !t.can_complete} onClick={() => run(() => interactionApi.editItem(it.id, t.id, { done: !t.done }))}>
                {t.done ? '✓' : ''}
              </button>
              <div className="vxi-row-body">
                {editingId === t.id ? (
                  <form
                    className="vxi-inline-form"
                    style={{ marginTop: 0 }}
                    onSubmit={(e) => {
                      e.preventDefault();
                      run(async () => {
                        await interactionApi.editItem(it.id, t.id, { body: editText });
                        setEditingId(0);
                      });
                    }}
                  >
                    <input type="text" value={editText} maxLength={300} onChange={(e) => setEditText(e.target.value)} aria-label="Task" autoFocus />
                    <button type="submit" className="vxi-btn small primary" disabled={busy || !editText.trim()}>Save</button>
                    <button type="button" className="vxi-btn small" onClick={() => setEditingId(0)}>Cancel</button>
                  </form>
                ) : (
                  t.body
                )}
                <div className="vxi-row-sub">
                  {t.assignee_name ? `👤 ${t.assignee_name}` : 'Unassigned'}
                  {t.due_at_ms ? ` · due ${formatWhen(t.due_at_ms)}${!t.done && t.due_at_ms < serverNow() ? ' (overdue)' : ''}` : ''}
                  {t.done && t.done_by_name ? ` · done by ${t.done_by_name}` : ''}
                  {t.source_user_name ? ` · from a message by ${t.source_user_name}` : ''}
                </div>
                {canEdit && expanded ? (
                  <div className="vxi-inline-form">
                    <select
                      className="vxi-input"
                      aria-label="Assign to"
                      value={t.assignee_user_id || ''}
                      onFocus={loadMembers}
                      onMouseDown={loadMembers}
                      onChange={(e) => run(() => interactionApi.editItem(it.id, t.id, { assignee_user_id: Number(e.target.value) || null }))}
                      style={{ flex: '1 1 140px' }}
                    >
                      <option value="">Unassigned</option>
                      {t.assignee_user_id && !(members || []).some((m) => m.id === t.assignee_user_id) ? <option value={t.assignee_user_id}>{t.assignee_name}</option> : null}
                      {(members || []).map((m) => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                    <input
                      type="date"
                      className="vxi-input"
                      aria-label="Due date"
                      style={{ flex: '0 1 150px' }}
                      value={t.due_at_ms ? new Date(t.due_at_ms - new Date(t.due_at_ms).getTimezoneOffset() * 60000).toISOString().slice(0, 10) : ''}
                      onChange={(e) => run(() => interactionApi.editItem(it.id, t.id, { due_at_ms: e.target.value ? new Date(`${e.target.value}T23:59`).getTime() : 0 }))}
                    />
                  </div>
                ) : null}
              </div>
              {canEdit ? (
                <div className="vxi-row-tools">
                  {expanded ? (
                    <>
                      <button type="button" className="vxi-btn small ghost" aria-label="Move up" disabled={busy || idx === 0} onClick={() => move(idx, -1)}>↑</button>
                      <button type="button" className="vxi-btn small ghost" aria-label="Move down" disabled={busy || idx === items.length - 1} onClick={() => move(idx, 1)}>↓</button>
                      <button type="button" className="vxi-btn small ghost" aria-label="Edit task" onClick={() => { setEditingId(t.id); setEditText(t.body); }}>✏️</button>
                    </>
                  ) : null}
                  <button type="button" className="vxi-btn small ghost" aria-label={`Remove ${t.body}`} disabled={busy} onClick={() => run(() => interactionApi.removeItem(it.id, t.id))}>✕</button>
                </div>
              ) : (
                <span />
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="vxi-empty">No tasks yet.</div>
      )}
      {!expanded && items.length > shown.length ? <div className="vxi-muted" style={{ marginTop: 4 }}>+ {items.length - shown.length} more — open Details</div> : null}
      {canEdit ? (
        <form
          className="vxi-inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!text.trim()) return;
            run(async () => {
              await interactionApi.addItem(it.id, { body: text });
              setText('');
            });
          }}
        >
          <input type="text" value={text} maxLength={300} placeholder="Add a task" aria-label="New task" onChange={(e) => setText(e.target.value)} />
          <button type="submit" className="vxi-btn" disabled={busy || !text.trim()}>Add</button>
        </form>
      ) : null}
    </>
  );
}

// ---------- Q&A ----------

function QaBody({ it, run, busy, expanded, setExpanded }) {
  const [text, setText] = useState('');
  const [anon, setAnon] = useState(false);
  const [sort, setSort] = useState('popular');
  const [answering, setAnswering] = useState(0);
  const [answer, setAnswer] = useState('');
  const list = useMemo(() => {
    const items = (it.items || []).slice();
    if (sort === 'newest') items.sort((a, b) => b.created_at_ms - a.created_at_ms);
    // Collapsed cards show the top few; the session itself stays in the expanded view.
    return expanded ? items : items.slice(0, 3);
  }, [it.items, sort, expanded]);
  const mod = !!it.perms.can_moderate;
  return (
    <>
      <div className="vxi-meta" style={{ marginTop: 4 }}>
        <span>{it.question_count} {it.question_count === 1 ? 'question' : 'questions'} · {it.answered_count} answered</span>
        {expanded ? (
          <span className="vxi-seg" role="group" aria-label="Sort questions">
            <button type="button" aria-pressed={sort === 'popular'} onClick={() => setSort('popular')}>Popular</button>
            <button type="button" aria-pressed={sort === 'newest'} onClick={() => setSort('newest')}>Newest</button>
          </span>
        ) : null}
      </div>
      {list.length ? (
        <div className={`vxi-rows${expanded ? ' vxi-scroll' : ''}`}>
          {list.map((q) => (
            <div key={q.id} className={`vxi-row${q.pinned ? ' is-pinned' : ''}`}>
              <button type="button" className="vxi-upvote" aria-pressed={q.upvoted} aria-label={`${q.upvoted ? 'Remove upvote' : 'Upvote'} (${q.upvotes})`} disabled={busy || !it.perms.can_submit} onClick={() => run(() => interactionApi.upvoteItem(it.id, q.id, !q.upvoted))}>
                ▲<span>{q.upvotes}</span>
              </button>
              <div className="vxi-row-body">
                {q.pinned ? '📌 ' : ''}
                {q.body}
                <div className="vxi-row-sub">
                  {q.anonymous ? 'Anonymous' : q.author_name}
                  {q.mine ? ' (you)' : ''}
                  {q.answered ? ' · ✅ answered' : ''}
                </div>
                {q.answer ? <div className="vxi-answer">{q.answer}</div> : null}
                {answering === q.id ? (
                  <form
                    className="vxi-inline-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      run(async () => {
                        await interactionApi.editItem(it.id, q.id, { answered: true, answer });
                        setAnswering(0);
                      });
                    }}
                  >
                    <input type="text" value={answer} maxLength={1000} placeholder="Answer (optional)" aria-label="Answer" onChange={(e) => setAnswer(e.target.value)} autoFocus />
                    <button type="submit" className="vxi-btn small primary" disabled={busy}>Mark answered</button>
                    <button type="button" className="vxi-btn small" onClick={() => setAnswering(0)}>Cancel</button>
                  </form>
                ) : null}
              </div>
              <div className="vxi-row-tools">
                {mod && expanded ? (
                  <>
                    <button type="button" className="vxi-btn small ghost" disabled={busy} onClick={() => (q.answered ? run(() => interactionApi.editItem(it.id, q.id, { answered: false })) : (setAnswering(q.id), setAnswer(q.answer || '')))}>
                      {q.answered ? 'Unanswer' : 'Answer'}
                    </button>
                    <button type="button" className="vxi-btn small ghost" disabled={busy} onClick={() => run(() => interactionApi.editItem(it.id, q.id, { pinned: !q.pinned }))}>
                      {q.pinned ? 'Unhighlight' : 'Highlight'}
                    </button>
                  </>
                ) : null}
                {(mod || (q.mine && !q.answered)) && expanded ? (
                  <button type="button" className="vxi-btn small ghost" aria-label="Remove question" disabled={busy} onClick={() => run(() => interactionApi.removeItem(it.id, q.id))}>✕</button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="vxi-empty">No questions yet{it.perms.can_submit ? ' — ask the first one.' : '.'}</div>
      )}
      {!expanded && it.question_count > list.length ? (
        <button type="button" className="vxi-link" style={{ marginTop: 6 }} onClick={() => setExpanded(true)}>
          Show all {it.question_count} questions
        </button>
      ) : null}
      {it.perms.can_submit ? (
        <form
          className="vxi-inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!text.trim()) return;
            run(async () => {
              await interactionApi.addItem(it.id, { body: text, anonymous: anon });
              setText('');
            });
          }}
        >
          <input type="text" value={text} maxLength={500} placeholder="Ask a question" aria-label="Your question" onChange={(e) => setText(e.target.value)} />
          <button type="submit" className="vxi-btn" disabled={busy || !text.trim()}>Ask</button>
          {it.config.allow_anonymous ? (
            <label className="vxi-toggle" style={{ margin: 0, fontSize: 12 }}>
              <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} /> Anonymous
            </label>
          ) : null}
        </form>
      ) : null}
    </>
  );
}

// ---------- suggestions ----------

function SuggestionBody({ it, run, busy, expanded, setExpanded }) {
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const s = it.suggestion;
  return (
    <>
      <div className="vxi-actions" style={{ marginTop: 6 }}>
        <span className="vxi-status-big" data-status={s.status}>{s.status_label}</span>
        <button type="button" className={`vxi-btn${it.my.upvoted ? ' active' : ''}`} aria-pressed={it.my.upvoted} disabled={busy || !it.perms.can_vote} onClick={() => run(() => (it.my.upvoted ? interactionApi.unvote(it.id) : interactionApi.vote(it.id, {})))}>
          ▲ {s.upvotes}
        </button>
        <button type="button" className={`vxi-btn${it.my.following ? ' active' : ''}`} aria-pressed={it.my.following} disabled={busy} onClick={() => run(() => interactionApi.subscribe(it.id, !it.my.following))}>
          {it.my.following ? '🔔 Following' : '🔕 Follow'}
        </button>
        <button type="button" className="vxi-btn ghost" onClick={() => setExpanded(!expanded)}>
          💬 {it.comment_count}
        </button>
      </div>
      {s.status_note ? <div className="vxi-answer">{s.status_note}</div> : null}
      {it.perms.can_set_status && expanded ? (
        <div className="vxi-inline-form">
          <select className="vxi-input" aria-label="Status" value={s.status} disabled={busy} onChange={(e) => run(() => interactionApi.status(it.id, e.target.value, note))} style={{ flex: '0 1 170px' }}>
            {SUGGESTION_STATUSES.map(([k, l]) => (
              <option key={k} value={k}>{l}</option>
            ))}
          </select>
          <input type="text" value={note} maxLength={300} placeholder="Note for followers (optional, sent with the next status change)" aria-label="Status note" onChange={(e) => setNote(e.target.value)} />
        </div>
      ) : null}
      {(it.items || []).length ? (
        <div className={`vxi-rows${expanded ? ' vxi-scroll' : ''}`}>
          {(expanded ? it.items : it.items.slice(-2)).map((c) => (
            <div key={c.id} className="vxi-row" style={{ gridTemplateColumns: 'minmax(0, 1fr) auto' }}>
              <div className="vxi-row-body">
                <strong style={{ fontSize: 12 }}>{c.author_name}</strong> {c.body}
              </div>
              {(c.mine || it.perms.can_manage) && expanded ? (
                <button type="button" className="vxi-btn small ghost" aria-label="Remove comment" disabled={busy} onClick={() => run(() => interactionApi.removeItem(it.id, c.id))}>✕</button>
              ) : <span />}
            </div>
          ))}
        </div>
      ) : null}
      {!expanded && it.comment_count > Math.min(2, (it.items || []).length) ? (
        <button type="button" className="vxi-link" style={{ marginTop: 6 }} onClick={() => setExpanded(true)}>
          Show all {it.comment_count} comments
        </button>
      ) : null}
      {it.perms.can_comment && expanded ? (
        <form
          className="vxi-inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!text.trim()) return;
            run(async () => {
              await interactionApi.addItem(it.id, { body: text });
              setText('');
            });
          }}
        >
          <input type="text" value={text} maxLength={1000} placeholder="Add a comment" aria-label="Comment" onChange={(e) => setText(e.target.value)} />
          <button type="submit" className="vxi-btn" disabled={busy || !text.trim()}>Comment</button>
        </form>
      ) : null}
    </>
  );
}

// ---------- countdowns ----------

function CountdownBody({ it, run, busy, now }) {
  const target = it.countdown.target_ms;
  const left = Math.max(0, target - now);
  const done = it.countdown.reached || left <= 0 || it.status === 'closed';
  const voiceId = it.config.voice_channel_id;
  const parts = [
    [Math.floor(left / 86400000), 'days'],
    [Math.floor((left % 86400000) / 3600000), 'hours'],
    [Math.floor((left % 3600000) / 60000), 'min'],
    [Math.floor((left % 60000) / 1000), 'sec'],
  ];
  return (
    <>
      {done ? (
        <div className="vxi-outcome approved" role="status">{it.countdown.ended_early ? 'This countdown was ended.' : '🎉 It’s time!'}</div>
      ) : (
        <div className="vxi-count" role="timer" aria-label={`${formatDuration(left)} left`}>
          {parts.map(([v, l]) => (
            <div key={l}>
              <strong>{String(v).padStart(2, '0')}</strong>
              <span>{l}</span>
            </div>
          ))}
        </div>
      )}
      <div className="vxi-actions">
        {!done ? (
          <button type="button" className={`vxi-btn${it.my.interested ? ' active' : ''}`} aria-pressed={it.my.interested} disabled={busy} onClick={() => run(() => interactionApi.subscribe(it.id, !it.my.interested))}>
            {it.my.interested ? '🔔 Reminding you' : '🔕 Remind me'} · {it.followers}
          </button>
        ) : null}
        {voiceId && (done || left < 15 * 60000) && !it.countdown.ended_early ? (
          <button type="button" className="vxi-btn primary" onClick={() => joinVoice(it, voiceId)}>🔊 Join voice</button>
        ) : null}
      </div>
      <div className="vxi-meta">
        <span>{formatWhen(target)}</span>
        {it.config.reminders && it.config.reminders.length && !done ? <span>Reminders {it.config.reminders.map((m) => (m === 0 ? 'at the time' : m >= 60 ? `${Math.round(m / 60)}h before` : `${m}m before`)).join(', ')}</span> : null}
      </div>
    </>
  );
}

// ---------- card ----------

export default function InteractionCard({ messageId, fallbackText = '' }) {
  const { interaction: summary, missing } = useInteraction(messageId);
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const { busy, err, run } = useBusy();
  const it = expanded && detail && summary && detail.version >= summary.version ? detail : summary;
  const ticking = !!it && (it.type === 'countdown' || it.type === 'event' || (it.open && !!it.closes_at_ms));
  const now = useServerClock(ticking);

  // Expanded cards fetch the detail view and follow every new version.
  useEffect(() => {
    if (!expanded || !summary) return undefined;
    if (detail && detail.version >= summary.version) return undefined;
    let alive = true;
    comsApiGet(`/interactions/${summary.id}`)
      .then((r) => alive && r && r.interaction && setDetail(r.interaction))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [expanded, summary, detail]);

  // A detail response from an action (vote, add item ...) is newer than the summary: keep it while expanded.
  const act = (fn) =>
    run(async () => {
      const r = await fn();
      if (r && r.interaction && expanded) setDetail(r.interaction);
      return r;
    });

  if (missing) return <div className="vxi-card is-closed"><div className="vxi-empty">This interaction is no longer available.{fallbackText ? ` (${fallbackText.split('\n')[0]})` : ''}</div></div>;
  if (!it) return <div className="vxi-card" aria-busy="true"><div className="vxi-skeleton" /></div>;
  const meta = TYPE_META[it.type] || { label: it.type, icon: '✨' };
  const Body = { poll: PollBody, decision: DecisionBody, event: EventBody, tasks: TasksBody, qa: QaBody, suggestion: SuggestionBody, countdown: CountdownBody }[it.type];
  const closed = it.status === 'closed';
  return (
    <section className={`vxi-card${closed ? ' is-closed' : ''}`} data-type={it.type} aria-label={`${meta.label}: ${it.title}`} data-testid={`interaction-card-${it.type}`}>
      <div className="vxi-head">
        <span className="vxi-type">
          <span aria-hidden="true">{meta.icon}</span> {meta.label}
        </span>
        <StatusChip it={it} now={now} />
        {it.config && it.config.anonymous ? <span className="vxi-chip">Anonymous</span> : null}
        {it.pinned ? <span className="vxi-chip">📌 Pinned</span> : null}
        <span className="vxi-spacer" />
        {it.voters != null && (it.type === 'poll' || it.type === 'decision' || (it.type === 'event' && it.status === 'open')) ? <span>{it.voters} {it.voters === 1 ? 'voter' : 'voters'}</span> : null}
      </div>
      {editing ? (
        <EditForm it={it} onDone={() => setEditing(false)} />
      ) : (
        <>
          <div className="vxi-title">{it.title}</div>
          {it.description ? <div className="vxi-desc">{it.description}</div> : null}
        </>
      )}
      {it.source ? (
        <div className="vxi-source">
          From a message by {it.source.user_name || 'a member'}
          {it.source.channel ? (
            <>
              {' · '}
              <button type="button" onClick={() => openTarget({ kind: 'message', server_key: it.server_key, channel_kind: it.source.channel.kind, channel_id: it.source.channel.id, message_id: it.source.message_id })}>
                view original
              </button>
            </>
          ) : null}
        </div>
      ) : null}
      {Body ? <Body it={it} run={act} busy={busy} now={now} expanded={expanded} setExpanded={setExpanded} /> : null}
      {it.type === 'poll' && !it.results_visible ? <div className="vxi-meta"><span>Results are shown when the poll closes</span></div> : null}
      {err ? <div className="vxi-error" role="alert">{err}</div> : null}
      <ManageBar it={it} expanded={expanded} setExpanded={setExpanded} onEdit={() => setEditing(true)} run={act} busy={busy} />
    </section>
  );
}
